'use client'

import * as React from 'react'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { DateRange } from 'react-day-picker'

import { cn } from './utils'
import { Button } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export interface DatePickerWithRangeProps {
  from?: Date
  to?: Date
  className?: string
  value?: DateRange
  onChange?: (value: DateRange | undefined) => void
  onBlur?: () => void
  name?: string
  fromDate?: Date
}

export const DatePickerWithRange: React.FC<DatePickerWithRangeProps> = ({
  className,
  from,
  to,
  value,
  onChange,
  onBlur,
  name,
  fromDate,
}) => {
  const [open, setOpen] = React.useState(false)
  // Use internal state if no value prop is provided (uncontrolled component)
  const [internalDate, setInternalDate] = React.useState<DateRange | undefined>(
    {
      from: from,
      to: to,
    }
  )

  // Determine whether to use controlled or uncontrolled state
  const date = value !== undefined ? value : internalDate

  // Handle date selection
  const handleSelect = (selectedDate: DateRange | undefined) => {
    // If we already have a full date range and user clicks on a new date,
    // reset the range to start a new selection
    if (date?.from && date?.to && selectedDate?.from && selectedDate?.to) {
      let newSelection = { from: selectedDate.to, to: undefined }
      if (date?.from !== selectedDate?.from) {
        newSelection = { from: selectedDate.from, to: undefined }
      }

      if (onChange) {
        onChange(newSelection)
      } else {
        setInternalDate(newSelection)
      }
    } else {
      if (onChange) {
        onChange(selectedDate)
      } else {
        setInternalDate(selectedDate)
      }

      // Close the popover if both from and to dates are selected
      if (selectedDate?.from && selectedDate?.to) {
        setOpen(false)
      }
    }

    // Call onBlur to notify form of change
    if (onBlur) {
      onBlur()
    }
  }

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={name}
            name={name}
            variant={'outline'}
            className={cn(
              'justify-start text-left font-normal',
              !date && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, 'LLL dd, y')} -{' '}
                  {format(date.to, 'LLL dd, y')}
                </>
              ) : (
                format(date.from, 'LLL dd, y')
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleSelect}
            numberOfMonths={2}
            fromDate={fromDate}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
